import type { Auth0ContextInterface, User } from '@auth0/auth0-react';
import {
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import LogRocket from 'logrocket';

import {
  MAX_URL_LENGTH,
  type QueryRouteBarrelTypes,
  QueryRouteBarrelVals,
  urlFromParams,
} from '@core/api';

import type { QueryError, ServerError } from '../utils/Query';
import { SlabConfig } from '../utils/SlabConfig';
import { useSlabAuth } from './useSlabAuth';

type Params = Record<string, object | string | number | boolean>;

type UrlFromParamsOpts = {
  queryParams: Params;
  pathParams: Params;
  urlSplits: string[];
};

export const UrlFromParams = (opts: UrlFromParamsOpts): string =>
  urlFromParams({
    ...opts,
    onUrlTooLong: ({ url, length }) => {
      LogRocket.error(
        'Generated a URL surpassing the maximum URL length for the minimum-allowed browser.',
        `Max Length allowed: ${MAX_URL_LENGTH}`,
        `Length encountered: ${length}`,
        url,
      );
    },
  });

export type ErrorResponse = {
  response?: {
    data: ServerError;
  };
};

/**
 * @returns the friendly error message to show the user from an error response.
 * Returns the response message if available, otherwise returns the default message if provided,
 * or undefined if neither exists.
 */
export const ServerErrorMessage = (
  error: unknown,
  fallbackMessage?: string,
): string | undefined => {
  if (isAxiosError(error)) {
    return error?.response?.data?.message ?? fallbackMessage;
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>)?.message === 'string'
  ) {
    return (error as { message: string }).message;
  }

  return fallbackMessage;
};

export type UseSlabQueryType<
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
> = {
  routeBarrelKey: TKey;
  args: TBarrel['args'];
  options?:
    | Omit<UseQueryOptions<TBarrel['returns'], TError, TBarrel['returns'], QueryKey>, 'queryKey'>
    | undefined;
};

// exported for testing
export const MakeQueryArgs = <
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
>(
  getAccessTokenSilently: Auth0ContextInterface<User>['getAccessTokenSilently'],
  routeBarrelKey: UseSlabQueryType<TKey, TBarrel, TError>['routeBarrelKey'],
  args: UseSlabQueryType<TKey, TBarrel, TError>['args'],
  options?: UseSlabQueryType<TKey, TBarrel, TError>['options'],
): Parameters<typeof useQuery<TBarrel['returns'], TError>>[0] => {
  const barrel = QueryRouteBarrelVals[routeBarrelKey];
  const fullUrl = UrlFromParams({
    queryParams: ((args as any)?.queryParams as Params) ?? {},
    pathParams: ((args as any)?.pathParams as Params) ?? {},
    urlSplits: barrel.urlSplits,
  });

  const getObject = async (signal?: AbortSignal): Promise<TBarrel['returns']> => {
    // Check the user has a valid access token
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: SlabConfig.auth0.audience,
      },
    });

    // Extract the raw ID_Token into the authoerization bearer header
    const headers = {
      headers: {
        ...(barrel.headers ?? {}),
        Authorization: `Bearer ${accessToken}`,
      },
    };

    const { data } = await axios.get(fullUrl, { ...headers, signal });
    return barrel.new(data);
  };

  return {
    ...options,
    queryKey: [fullUrl, routeBarrelKey],
    queryFn: ({ signal }) => getObject(signal),
  };
};

export const useSlabQuery = <
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
>(
  routeBarrelKey: UseSlabQueryType<TKey, TBarrel, TError>['routeBarrelKey'],
  args: UseSlabQueryType<TKey, TBarrel, TError>['args'],
  options?: UseSlabQueryType<TKey, TBarrel, TError>['options'],
): UseQueryResult<TBarrel['returns'], TError> => {
  const { getAccessTokenSilently } = useSlabAuth();
  return useQuery<TBarrel['returns'], TError>(
    MakeQueryArgs(getAccessTokenSilently, routeBarrelKey, args, options),
  );
};

export const useSlabQueryFetcher = <
  TKey extends keyof QueryRouteBarrelTypes,
  TBarrel extends QueryRouteBarrelTypes[TKey] = QueryRouteBarrelTypes[TKey],
  TError extends QueryError = QueryError,
>(): ((
  routeBarrelKey: UseSlabQueryType<TKey, TBarrel, TError>['routeBarrelKey'],
  args: UseSlabQueryType<TKey, TBarrel, TError>['args'],
  options?: Omit<UseSlabQueryType<TKey, TBarrel, TError>['options'], 'refetchInterval'>,
) => Promise<TBarrel['returns']>) => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useSlabAuth();
  return (routeBarrelKey, args, options) =>
    queryClient.fetchQuery(MakeQueryArgs(getAccessTokenSilently, routeBarrelKey, args, options));
};
